import _ from 'lodash'
import { ISSMLCheckError } from 'ssml-check'

import { ObjectSsmlValidationResult, PropertySsmlValidationResult } from '../../types'
import { doesIssueSatisfySchema, TagIssueConstraintSchema } from './tagIssueSchemaEngine'
import { isTagIssue } from '../general'
import { not } from '../../../../lib/functionChaining'

const TREAT_AS_WARNINGS_SCHEMA: TagIssueConstraintSchema = [
  { tag: 'mstts:ttsbreak', constraints: [{ type: 'always' }] },
  {
    tag: 'say-as',
    constraints: {
      'interpret-as': [
        {
          type: 'values',
          enum: new Set([
            'url',
            'math',
            'name',
            'email',
            'spell',
            'address',
            'message',
            'currency',
            'duration',
            'number_digit'
          ])
        }
      ],
      format: [
        { type: 'values', enum: new Set(['.', ',', '1', 'ms', 'hm', 'hms', 'hms12', 'undefined']) }
      ]
    }
  }
]

const getSchemaForTag = _.memoize((tag: string) =>
  TREAT_AS_WARNINGS_SCHEMA.find(item => item.tag.toLowerCase() === tag.toLowerCase())
)

export const shouldIssueBeTreatedAsWarning = (issue: ISSMLCheckError) => {
  if (isTagIssue(issue)) {
    return doesIssueSatisfySchema(issue, getSchemaForTag(issue.tag))
  }
}

export function containsOnlyWarnings(ssmlResult?: PropertySsmlValidationResult): boolean {
  const issues = ssmlResult?.errors ?? []
  return issues.every(shouldIssueBeTreatedAsWarning)
}

function isPropertyValidationResult(obj: object): obj is PropertySsmlValidationResult {
  // @ts-ignore
  return !!obj?.__isPropertyValidationResult
}

type ResultWithError<T extends PropertySsmlValidationResult | ObjectSsmlValidationResult> =
  T extends PropertySsmlValidationResult
    ? PropertySsmlValidationResult & { errors: ISSMLCheckError[] }
    : ObjectSsmlValidationResult

export function containsAtLeastOneError<
  T extends PropertySsmlValidationResult | ObjectSsmlValidationResult
  // @ts-ignore
>(propertyOrObjResult: T): propertyOrObjResult is ResultWithError<T> {
  if (isPropertyValidationResult(propertyOrObjResult)) {
    return (propertyOrObjResult?.errors ?? []).some(not(shouldIssueBeTreatedAsWarning))
  }

  return Object.values(propertyOrObjResult).some(res => !!res && containsAtLeastOneError(res))
}

export function hasNotEmptyErrors(ssmlResult?: PropertySsmlValidationResult): boolean {
  return !!ssmlResult?.errors?.length
}

export function hasEmptyErrors(ssmlResult?: PropertySsmlValidationResult): boolean {
  return !hasNotEmptyErrors(ssmlResult)
}
